import { Provider } from '@vgtv/api-client';

import { config } from '../../config.js';

import { rawTrackEngagementEvent } from './pulse.js';
import {
  getCurrentPageViewData,
  getDurationSincePageView,
  getPageViewOrigin,
} from './helpers/pulseOrigin.js';
import {
  Brand,
  getElementSDRN,
  getSDRN,
  getSubscriptionSDRN,
} from './helpers/getSDRN.js';

import type { PulseEventObject } from './pulse.js';
import type { BrandSubscriptionMap } from './helpers/getSDRN.js';

export async function trackButtonClickEvent(
  button: Pick<PulseEventObject, 'elementType' | 'position'> & {
    title: string;
  },
  target: { elementType: string; name: string; url: string },
  parent?: { elementType: string; name: string; row?: number }
) {
  const object = prepareButtonClickEvent(button, target, parent);

  return rawTrackEngagementEvent({ object });
}

export const trackImpressionEvent = (object: Partial<PulseEventObject>) => {
  return rawTrackEngagementEvent({
    intent: 'View',
    object: {
      ...object,
      type: 'UIElement',
    },
  });
};

export const trackLoginClickEvent = ({
  button,
  parent,
}: {
  button: Pick<PulseEventObject, 'elementType' | 'contentId'> & {
    title: string;
  };
  parent?: {
    elementType: string;
    name: string;
  };
}) => {
  const object = prepareButtonClickEvent(button, undefined, parent);

  rawTrackEngagementEvent({
    action: 'Click',
    intent: 'Login',
    object,
  });
};

export interface ProviderSubscriptionMap {
  [Provider.VGTV]: BrandSubscriptionMap['vg'];
  [Provider.AB]: BrandSubscriptionMap['aftonbladet'];
}

export const trackSubscribeClickEvent = async <T extends Provider>({
  button,
  parent,
  subscription,
  xDomainId,
}: {
  button: {
    elementIdentifier?: string;
    elementType: string;
    title: string;
  };
  parent?: {
    elementType: string;
    name: string;
  };
  subscription: {
    provider: T;
    price?: number;
    name: ProviderSubscriptionMap[T];
    url: string;
  };
  xDomainId?: string;
}) => {
  const object = prepareButtonClickEvent(button, undefined, parent);

  const brand =
    subscription.provider === Provider.VGTV ? Brand.VG : Brand.AFTONBLADET;

  const target = {
    '@id': getSubscriptionSDRN(brand, subscription.name),
    '@type': 'Subscription',
    channel: 'online',
    currency: subscription.provider === Provider.VGTV ? 'NOK' : 'SEK',
    deliveryType: 'Digital',
    price: subscription.price,
    quantity: 1,
    url: subscription.url,
  };

  return rawTrackEngagementEvent(
    {
      action: 'Click',
      intent: 'Purchase',
      object,
      target,
    },
    xDomainId
  );
};

export function trackClickEvent(
  parent?: Parameters<typeof prepareClickEvent>[0],
  object?: Parameters<typeof prepareClickEvent>[1],
  target?: Parameters<typeof prepareClickEvent>[2]
) {
  if (!parent || !object || !target) {
    return;
  }
  const trackingObject = prepareClickEvent(parent, object, target);

  rawTrackEngagementEvent({ object: trackingObject });
}

export function prepareClickEvent(
  parent: {
    name?: string;
    skin?: string;
    row?: number;
    elementType: string;
  },
  object: {
    title: string;
    elementType: string;
    id: string | number;
    position?: number | string;
  },
  target: {
    type: string;
    title: string;
    url?: string;
  }
) {
  const currentOrigin = getPageViewOrigin();
  const currentPageViewData = getCurrentPageViewData();

  const trackingObject = {
    '@id': getElementSDRN(currentOrigin['@id']!, object.elementType, object.id),
    '@type': 'UIElement',
    name: object.title,
    url: currentPageViewData.url,
    elementType: object.elementType,
    duration: getDurationSincePageView(),
    ...('position' in object && { position: String(object.position) }),
    ...(parent.skin && { skin: parent.skin }),
    ...(currentPageViewData.object?.category && {
      section: currentPageViewData.object.category,
    }),
    parent: {
      '@id': getElementSDRN(currentOrigin['@id']!, parent.elementType),
      '@type': 'UIElement',
      ...(parent.name && { name: parent.name }),
      url: currentPageViewData.url,
      elementType: parent.elementType,
      ...('row' in parent && { row: parent.row }),
    },
    target: {
      '@id': getSDRN(config.pulse.site, target.type.toLowerCase(), object.id),
      '@type': target.type,
      name: target.title,
      url: target.url,
    },
  };

  return trackingObject;
}

function prepareButtonClickEvent(
  button: Pick<PulseEventObject, 'elementType' | 'contentId' | 'position'> & {
    title: string;
    elementIdentifier?: string;
  },
  target?: { elementType: string; name: string; url: string },
  parent?: { elementType: string; name: string; row?: number }
) {
  const currentOrigin = getPageViewOrigin();
  const currentPageViewData = getCurrentPageViewData();

  const trackingObject = {
    '@id': getElementSDRN(
      currentOrigin['@id']!,
      button.elementIdentifier ?? button.elementType!
    ),
    '@type': 'UIElement',
    name: button.title,
    url: currentPageViewData.url,
    elementType: button.elementType,
    contentId: button.contentId,
    duration: getDurationSincePageView(),
    ...(currentPageViewData.object?.category && {
      section: currentPageViewData.object.category,
    }),
    ...(button.position && { position: String(button.position) }),
    target: target && {
      '@id': getElementSDRN(currentOrigin['@id']!, target.elementType),
      '@type': 'UIElement',
      name: target.name,
      url: target.url,
    },
    parent: parent && {
      '@id': getElementSDRN(currentOrigin['@id']!, parent.elementType),
      '@type': 'UIElement',
      name: parent.name,
      url: currentPageViewData.url,
      elementType: parent.elementType,
    },
  };

  return trackingObject;
}
